/**
 * TtStockDashboard — read-only reporting page for a TT stock engine.
 *
 * Calls /admin/tt-stock-report. Shows engine config, open positions,
 * closed P&L, per-strategy / per-symbol breakdown, regime overlay impact.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { tradeSignalsService } from '../../services/tradeSignalsService';
import '../Dashboard.css';

const c = {
  bg: '#0f172a',
  card: '#1f2937',
  cardAlt: '#1a2332',
  border: '#374151',
  text: '#f9fafb',
  muted: '#9ca3af',
  dim: '#6b7280',
  green: '#16a34a',
  greenBg: '#16a34a20',
  red: '#dc2626',
  redBg: '#dc262620',
  amber: '#d97706',
  amberBg: '#d9770620',
  purple: '#8b5cf6',
  purpleBg: '#8b5cf620',
};

const fmtMoney = (n: number | null | undefined) => {
  if (n == null) return '—';
  const sign = n < 0 ? '−' : '';
  const abs = Math.abs(n);
  return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtPct = (n: number | null | undefined) => n == null ? '—' : `${n.toFixed(1)}%`;
const colorFor = (n: number | null | undefined) => n == null ? c.muted : n > 0 ? c.green : n < 0 ? c.red : c.text;

interface Trade {
  tradeId: string;
  symbol: string;
  side: string;
  qty: number;
  strategy_name: string;
  entryAt: string;
  filledPrice: number;
  suggestedStop: number;
  suggestedTarget: number;
  trailingStop?: number | null;
  currentPrice?: number;
  unrealizedPnl?: number;
  status: string;
  exitAt?: string;
  exitPrice?: number;
  exitReason?: string;
  pnl?: number;
  base_score?: number;
  score?: number;
  regime_multiplier?: number;
  regime_adjustments?: Array<{ factor: string; value: number; reason: string }>;
  rationale?: string;
  // Option-aware fields (present on manual-mirror rows, optional on auto-trader)
  optionType?: 'P' | 'C';
  direction?: 'long' | 'short';
  displaySymbol?: string;
  contractSymbol?: string;
  source?: string;
  assetClass?: 'stocks' | 'options';
}

// Underlying-market direction. For stocks, long=bullish/short=bearish.
// For options, factor in put/call:
//   long call  -> bullish (you profit if stock goes UP)
//   long put   -> bearish (you profit if stock goes DOWN)
//   short call -> bearish (you lose if stock goes UP)
//   short put  -> bullish (you lose if stock goes DOWN)
//
// Falls back to the buy/sell heuristic when option fields aren't set
// (e.g. legacy auto-trader rows from the equity-only engine).
function underlyingDirection(t: Trade): { tag: string; color: string; label: string } {
  const isOption = t.optionType === 'P' || t.optionType === 'C';
  if (isOption) {
    const isShort = t.direction === 'short';
    const isCall  = t.optionType === 'C';
    // bullish iff (long call) OR (short put)
    const bullish = (isCall && !isShort) || (!isCall && isShort);
    return bullish
      ? { tag: '↑', color: c.green, label: isShort ? `SHORT ${t.optionType}` : `LONG ${t.optionType}` }
      : { tag: '↓', color: c.red,   label: isShort ? `SHORT ${t.optionType}` : `LONG ${t.optionType}` };
  }
  // Stock fallback -- use side.
  const isBuy = t.side === 'buy' || t.direction === 'long';
  return isBuy
    ? { tag: '↑', color: c.green, label: 'BUY'  }
    : { tag: '↓', color: c.red,   label: 'SELL' };
}

interface Policy {
  policyId: string;
  strategyId: string;
  riskAppetite: string;
  symbols: string[];
}

interface LiveEquity {
  netLiq: number;
  equityBP: number;
  derivativeBP: number;
  cashBalance: number;
  accountNumber: string;
  snapshotTime: string;
  ageMs: number;
}

interface Report {
  engine: {
    engineId: string;
    name: string;
    status: string;
    paperMode: boolean;
    venue: string;
    startingEquity: number;
    liveStartingEquity?: number | null;
    liveStartedAt?: string | null;
    currentEquity: number;
    regimeOverlay: boolean;
    policies: Policy[];
    tastytradeAccountNumber?: string | null;
    tastytradeConnectionLabel?: string | null;
    liveEquity?: LiveEquity | null;
  };
  stats: {
    counts: { total: number; open: number; closed: number; wins: number; losses: number };
    pnl: {
      realized: number; unrealized: number; total: number;
      avgWin: number | null; avgLoss: number | null; expectancy: number | null;
      bestTrade: number | null; worstTrade: number | null;
    };
    winRate: number | null;
    cumulativeByDate: Array<{ date: string; pnl: number; symbol: string }>;
    byStrategy: Record<string, { n: number; wins: number; pnl: number }>;
    bySymbol: Record<string, { n: number; wins: number; pnl: number }>;
    byExitReason: Record<string, { n: number; pnl: number }>;
    regimeImpact: {
      avgMultiplierOnWinners: number | null;
      avgMultiplierOnLosers: number | null;
      delta: number | null;
      interpretation: string;
    } | null;
  };
  open: Trade[];
  closed: Trade[];
  generatedAt: string;
}

const TtStockDashboard: React.FC = () => {
  const { token } = useAuth();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [policyExpanded, setPolicyExpanded] = useState<Set<string>>(new Set());

  const load = async () => {
    if (!token) return;
    try {
      setLoading(true);
      setErr(null);
      const r = await tradeSignalsService.getTtStockReport(token);
      setReport(r as Report);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const toggleRow = (id: string) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const togglePolicy = (id: string) => {
    setPolicyExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Navigation />
        <div className="dashboard-content" style={{ padding: '2rem', color: c.muted }}>Loading…</div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="dashboard-container">
        <Navigation />
        <div className="dashboard-content" style={{ padding: '2rem' }}>
          <div style={{ background: c.redBg, border: `1px solid ${c.red}`, color: c.red, padding: '1rem', borderRadius: 8 }}>{err}</div>
        </div>
      </div>
    );
  }
  if (!report) return null;

  const { engine, stats, open, closed } = report;
  const equityChange = engine.currentEquity - engine.startingEquity;
  const equityChangePct = (equityChange / engine.startingEquity) * 100;

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content" style={{ padding: '1.5rem', maxWidth: 1400, margin: '0 auto', color: c.text }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>
              {engine.name}
              <span style={{ marginLeft: '0.6rem', fontSize: '0.7rem', padding: '0.2rem 0.55rem', borderRadius: 4,
                              background: engine.paperMode ? c.amberBg : c.greenBg,
                              color: engine.paperMode ? c.amber : c.green, fontWeight: 700, letterSpacing: '0.04em' }}>
                {engine.paperMode ? 'PAPER' : 'LIVE'}
              </span>
            </h1>
            <p style={{ margin: '0.3rem 0 0', color: c.muted, fontSize: '0.85rem' }}>
              {engine.venue} · {engine.status} · regime overlay {engine.regimeOverlay ? 'on' : 'off'}
              {engine.tastytradeAccountNumber && ` · TT ${engine.tastytradeAccountNumber}`}
            </p>
          </div>
          <button onClick={load} disabled={loading} style={{ padding: '0.5rem 1rem', background: c.purple, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>↻ Refresh</button>
        </div>

        {/* Account snapshot */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
          <KPI
            label={engine.liveStartingEquity ? 'Starting (day-1 live)' : 'Starting'}
            value={fmtMoney(engine.startingEquity)}
            sublabel={engine.liveStartedAt ? `since ${new Date(engine.liveStartedAt).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })}` : undefined}
          />
          <KPI label={engine.liveEquity ? 'NetLiq (live)' : 'Current (paper)'} value={fmtMoney(engine.currentEquity)} valueColor={colorFor(equityChange)} />
          <KPI label="Realized P&L" value={fmtMoney(stats.pnl.realized)} valueColor={colorFor(stats.pnl.realized)} />
          <KPI label="Unrealized" value={fmtMoney(stats.pnl.unrealized)} valueColor={colorFor(stats.pnl.unrealized)} />
          <KPI label="Total return" value={`${equityChangePct >= 0 ? '+' : ''}${equityChangePct.toFixed(2)}%`} valueColor={colorFor(equityChange)} />
          <KPI label="Open / Closed" value={`${stats.counts.open} / ${stats.counts.closed}`} />
        </div>

        {/* Live broker equity (only for live engines with a fresh TT snapshot) */}
        {engine.liveEquity && (
          <div style={{ background: c.cardAlt, border: `1px solid ${c.green}`, borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '1rem' }}>
            <strong style={{ color: c.green, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Live broker account ({engine.liveEquity.accountNumber})</strong>
            <div style={{ marginTop: '0.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.6rem', fontSize: '0.86rem' }}>
              <div><span style={{ color: c.muted }}>NetLiq:</span> <strong>{fmtMoney(engine.liveEquity.netLiq)}</strong></div>
              <div><span style={{ color: c.muted }}>Equity BP (stock sizing base):</span> <strong style={{ color: c.green }}>{fmtMoney(engine.liveEquity.equityBP)}</strong></div>
              <div><span style={{ color: c.muted }}>Derivative BP (options):</span> <strong>{fmtMoney(engine.liveEquity.derivativeBP)}</strong></div>
              <div><span style={{ color: c.muted }}>Cash:</span> <strong>{fmtMoney(engine.liveEquity.cashBalance)}</strong></div>
            </div>
            <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: c.dim, fontStyle: 'italic' }}>
              Snapshot {Math.round(engine.liveEquity.ageMs / 60000)} min ago · sized at moderate 5% per trade ≈ {fmtMoney(engine.liveEquity.equityBP * 0.05)}/position
            </div>
          </div>
        )}

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
          <KPI label="Win rate" value={fmtPct(stats.winRate)} />
          <KPI label="Avg winner" value={fmtMoney(stats.pnl.avgWin)} valueColor={c.green} />
          <KPI label="Avg loser" value={fmtMoney(stats.pnl.avgLoss)} valueColor={c.red} />
          <KPI label="Expectancy" value={fmtMoney(stats.pnl.expectancy)} />
          <KPI label="Best trade" value={fmtMoney(stats.pnl.bestTrade)} valueColor={c.green} />
          <KPI label="Worst trade" value={fmtMoney(stats.pnl.worstTrade)} valueColor={c.red} />
        </div>

        {/* Engine config (policies) */}
        <section style={{ marginBottom: '1rem' }}>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem', fontWeight: 700 }}>
            Engine ({engine.policies?.length || 0} {(engine.policies?.length || 0) === 1 ? 'policy' : 'policies'})
          </h2>
          {!engine.policies || engine.policies.length === 0 ? (
            <div style={{ color: c.muted, fontSize: '0.9rem', padding: '0.75rem', background: c.cardAlt, borderRadius: 6 }}>No policies configured.</div>
          ) : (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {engine.policies.map((p) => (
                <PolicyCard
                  key={p.policyId}
                  policy={p}
                  expanded={policyExpanded.has(p.policyId)}
                  onToggle={() => togglePolicy(p.policyId)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Regime overlay impact */}
        {stats.regimeImpact && (
          <div style={{ background: c.purpleBg, border: `1px solid ${c.purple}`, borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '1rem' }}>
            <strong style={{ color: c.purple, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Regime Overlay Impact</strong>
            <div style={{ marginTop: '0.4rem', display: 'flex', gap: '2rem', flexWrap: 'wrap', fontSize: '0.86rem' }}>
              <div>Avg multiplier on winners: <strong>{stats.regimeImpact.avgMultiplierOnWinners ?? '—'}</strong></div>
              <div>Avg multiplier on losers: <strong>{stats.regimeImpact.avgMultiplierOnLosers ?? '—'}</strong></div>
              {stats.regimeImpact.delta != null && (
                <div>Delta: <strong style={{ color: stats.regimeImpact.delta > 0 ? c.green : c.red }}>{stats.regimeImpact.delta > 0 ? '+' : ''}{stats.regimeImpact.delta}</strong></div>
              )}
            </div>
            <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: c.muted, fontStyle: 'italic' }}>{stats.regimeImpact.interpretation}</div>
          </div>
        )}

        {/* Open positions — dense table */}
        <section style={{ marginBottom: '1.25rem' }}>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem', fontWeight: 700 }}>Open positions ({open.length})</h2>
          {open.length === 0 ? (
            <div style={{ color: c.muted, fontSize: '0.9rem', padding: '0.75rem', background: c.cardAlt, borderRadius: 6 }}>No open positions.</div>
          ) : (
            <TradesTable trades={open} mode="open" expanded={expanded} onToggle={toggleRow} />
          )}
        </section>

        {/* Closed positions — dense table */}
        <section style={{ marginBottom: '1.25rem' }}>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem', fontWeight: 700 }}>Closed positions ({closed.length})</h2>
          {closed.length === 0 ? (
            <div style={{ color: c.muted, fontSize: '0.9rem', padding: '0.75rem', background: c.cardAlt, borderRadius: 6 }}>No closed positions yet.</div>
          ) : (
            <TradesTable trades={closed.slice(0, 50)} mode="closed" expanded={expanded} onToggle={toggleRow} />
          )}
        </section>

        {/* Per-strategy + per-symbol breakdown */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
          <BreakdownTable title="By strategy" rows={Object.entries(stats.byStrategy)} />
          <BreakdownTable title="By symbol" rows={Object.entries(stats.bySymbol).sort((a, b) => b[1].pnl - a[1].pnl).slice(0, 12)} />
        </div>

        {/* Footer */}
        <div style={{ color: c.dim, fontSize: '0.75rem', textAlign: 'right' }}>
          Generated {new Date(report.generatedAt).toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', month: 'short', day: 'numeric' })} ET
        </div>
      </div>
    </div>
  );
};

const KPI: React.FC<{ label: string; value: string; valueColor?: string; sublabel?: string }> = ({ label, value, valueColor, sublabel }) => (
  <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6, padding: '0.7rem 0.85rem' }}>
    <div style={{ fontSize: '0.65rem', color: c.dim, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
    <div style={{ marginTop: 4, fontSize: '1.1rem', fontWeight: 700, color: valueColor || c.text }}>{value}</div>
    {sublabel && <div style={{ marginTop: 2, fontSize: '0.68rem', color: c.dim }}>{sublabel}</div>}
  </div>
);

// Dense table view for trade lists. Click a row to expand to the rich
// detail (reusing the same TradeCard the prior layout used). Replaces the
// card-only layout requested 2026-06-04 ("we should add open and closed
// trades table"). 12 cols max to keep it readable on a 1400px container.
const TradesTable: React.FC<{
  trades: Trade[];
  mode: 'open' | 'closed';
  expanded: Set<string>;
  onToggle: (id: string) => void;
}> = ({ trades, mode, expanded, onToggle }) => {
  const cols = mode === 'open'
    ? ['Symbol', 'Strategy', 'Side', 'Qty', 'Entry', 'Current', 'Stop', 'Target', 'Unreal P&L', 'Score', 'Opened', '']
    : ['Symbol', 'Strategy', 'Side', 'Qty', 'Entry', 'Exit', 'Reason', 'P&L', 'Score', 'Opened', 'Closed', ''];

  return (
    <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        <thead>
          <tr style={{ background: c.cardAlt }}>
            {cols.map((h) => (
              <th key={h} style={{
                padding: '0.5rem 0.6rem', textAlign: 'left', fontSize: '0.66rem',
                fontWeight: 700, color: c.muted, letterSpacing: '0.05em',
                textTransform: 'uppercase', borderBottom: `1px solid ${c.border}`,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trades.map((t) => {
            const isExp = expanded.has(t.tradeId);
            const pnl = mode === 'open' ? (t.unrealizedPnl || 0) : (t.pnl || 0);
            const pnlColor = pnl > 0 ? c.green : pnl < 0 ? c.red : c.muted;
            const dir = underlyingDirection(t);
            return (
              <React.Fragment key={t.tradeId}>
                <tr
                  onClick={() => onToggle(t.tradeId)}
                  style={{
                    cursor: 'pointer', borderBottom: `1px solid ${c.border}`,
                    background: isExp ? c.cardAlt : 'transparent',
                  }}
                >
                  <td style={{ padding: '0.5rem 0.6rem', fontWeight: 700 }}>
                    {t.displaySymbol || t.symbol}
                  </td>
                  <td style={{ padding: '0.5rem 0.6rem', color: c.muted, fontSize: '0.75rem' }}>{t.strategy_name}</td>
                  <td style={{ padding: '0.5rem 0.6rem', color: dir.color, fontWeight: 600 }}>
                    <span style={{ marginRight: 4 }}>{dir.tag}</span>{dir.label}
                  </td>
                  <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t.qty}</td>
                  <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>${t.filledPrice?.toFixed(2)}</td>
                  {mode === 'open' ? (
                    <>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t.currentPrice ? `$${t.currentPrice.toFixed(2)}` : '—'}</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', color: c.red, fontVariantNumeric: 'tabular-nums' }}>${t.suggestedStop?.toFixed(2)}</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', color: c.green, fontVariantNumeric: 'tabular-nums' }}>${t.suggestedTarget?.toFixed(2)}</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', color: pnlColor, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t.score?.toFixed(0) ?? '—'}</td>
                      <td style={{ padding: '0.5rem 0.6rem', color: c.muted, fontSize: '0.72rem' }}>{t.entryAt ? new Date(t.entryAt).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' }) : '—'}</td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t.exitPrice ? `$${t.exitPrice.toFixed(2)}` : '—'}</td>
                      <td style={{ padding: '0.5rem 0.6rem', color: c.muted, fontSize: '0.72rem' }}>{t.exitReason || '—'}</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', color: pnlColor, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t.score?.toFixed(0) ?? '—'}</td>
                      <td style={{ padding: '0.5rem 0.6rem', color: c.muted, fontSize: '0.72rem' }}>{t.entryAt ? new Date(t.entryAt).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' }) : '—'}</td>
                      <td style={{ padding: '0.5rem 0.6rem', color: c.muted, fontSize: '0.72rem' }}>{t.exitAt ? new Date(t.exitAt).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' }) : '—'}</td>
                    </>
                  )}
                  <td style={{ padding: '0.5rem 0.6rem', textAlign: 'center', color: c.muted, fontSize: '0.7rem' }}>{isExp ? '▼' : '▶'}</td>
                </tr>
                {isExp && (
                  <tr>
                    <td colSpan={cols.length} style={{ background: c.bg, padding: '0.6rem' }}>
                      <TradeCard trade={t} expanded={true} onToggle={() => onToggle(t.tradeId)} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TradeCard: React.FC<{ trade: Trade; expanded: boolean; onToggle: () => void }> = ({ trade, expanded, onToggle }) => {
  const isClosed = trade.status === 'closed';
  const pnl = isClosed ? trade.pnl : trade.unrealizedPnl;
  const dir = underlyingDirection(trade);
  return (
    <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6 }}>
      <div onClick={onToggle} style={{
        cursor: 'pointer', padding: '0.7rem 0.9rem',
        display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) minmax(180px, 2fr) minmax(120px, 1fr) auto',
        gap: '0.75rem', alignItems: 'center',
      }}>
        <div>
          <span style={{ fontSize: '1rem', fontWeight: 700, color: c.text }}>
            {trade.displaySymbol || trade.symbol}
          </span>
          <span style={{ marginLeft: 6, fontSize: '0.85rem', color: dir.color, fontWeight: 700 }}>{dir.tag}</span>
          <div style={{ fontSize: '0.72rem', color: c.muted, marginTop: 2 }}>{trade.strategy_name}</div>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.82rem' }}>
          <Cell label="Qty" value={String(trade.qty)} />
          <Cell label="Entry" value={fmtMoney(trade.filledPrice)} />
          <Cell label={isClosed ? 'Exit' : 'Mark'} value={fmtMoney(isClosed ? trade.exitPrice : trade.currentPrice)} />
          {!isClosed && <Cell label="Stop" value={fmtMoney(trade.trailingStop ?? trade.suggestedStop)} />}
          {isClosed && <Cell label="Reason" value={trade.exitReason || '—'} valueColor={c.muted} />}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.62rem', color: c.dim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{isClosed ? 'P&L' : 'Unrealized'}</div>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, color: colorFor(pnl) }}>{fmtMoney(pnl)}</div>
        </div>
        <div style={{ fontSize: '0.78rem', color: c.muted }}>{expanded ? '▼' : '▶'}</div>
      </div>
      {expanded && (
        <div style={{ padding: '0 0.9rem 0.85rem', borderTop: `1px solid ${c.border}`, background: c.cardAlt }}>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '0.6rem', fontSize: '0.78rem' }}>
            <Cell label="Stop" value={fmtMoney(trade.suggestedStop)} />
            <Cell label="Target" value={fmtMoney(trade.suggestedTarget)} />
            <Cell label="Base score" value={String(trade.base_score ?? '—')} />
            <Cell label="Final score" value={String(trade.score ?? '—')} />
            <Cell label="Regime mult" value={trade.regime_multiplier?.toFixed(3) || '—'} />
            <Cell label="Entry at" value={(trade.entryAt || '').slice(0, 16).replace('T', ' ')} />
            {isClosed && <Cell label="Exit at" value={(trade.exitAt || '').slice(0, 16).replace('T', ' ')} />}
          </div>
          {trade.regime_adjustments && trade.regime_adjustments.length > 0 && (
            <div style={{ marginTop: '0.7rem' }}>
              <div style={{ fontSize: '0.7rem', color: c.muted, fontWeight: 600, marginBottom: 4 }}>Regime overlay</div>
              {trade.regime_adjustments.map((a, i) => {
                const friendly = friendlyRegime(a.factor, a.reason);
                const pct = (a.value * 100).toFixed(1);
                return (
                  <div key={i} style={{ fontSize: '0.78rem', color: c.text, marginBottom: 2 }}>
                    <span style={{ display: 'inline-block', width: 90, color: c.muted, fontWeight: 600 }}>{friendly.label}</span>
                    <span style={{ color: a.value > 0 ? c.green : c.red, fontWeight: 600, display: 'inline-block', width: 50 }}>{a.value > 0 ? '+' : ''}{pct}%</span>
                    <span style={{ color: c.muted }}>{friendly.desc}</span>
                  </div>
                );
              })}
            </div>
          )}
          {trade.rationale && (
            <div style={{ marginTop: '0.7rem', padding: '0.5rem 0.7rem', background: c.bg, borderRadius: 4, fontSize: '0.78rem', whiteSpace: 'pre-wrap', color: c.muted, lineHeight: 1.5 }}>
              {trade.rationale}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Cell: React.FC<{ label: string; value: string; valueColor?: string }> = ({ label, value, valueColor }) => (
  <div>
    <div style={{ fontSize: '0.62rem', color: c.dim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ fontWeight: 600, color: valueColor || c.text }}>{value}</div>
  </div>
);

// Translate raw regime-overlay strings into plain English so traders see
// the conclusion, not the formula. Hides z-scores, penalty magnitudes,
// gamma-flip percentages. Per feedback-plain-english + feedback-hide-formula.
function friendlyRegime(factor: string, reason: string): { label: string; desc: string } {
  const r = (reason || '').toLowerCase();
  if (factor === 'macro') {
    if (r.includes('calm')) return { label: 'Macro', desc: 'Calm market — no stress drag' };
    if (r.includes('mild') || r.includes('elevated')) return { label: 'Macro', desc: 'Mild stress — small drag' };
    if (r.includes('stressed') || r.includes('crisis')) return { label: 'Macro', desc: 'Stressed market — penalty applied' };
    return { label: 'Macro', desc: 'Background macro climate' };
  }
  if (factor === 'gex') {
    if (r.includes('above flip') && r.includes('long-gamma')) return { label: 'Options flow', desc: 'Above gamma flip — dealers stabilizing' };
    if (r.includes('below flip') && r.includes('short-gamma')) return { label: 'Options flow', desc: 'Below gamma flip — dealers amplifying moves' };
    if (r.includes('near flip') || r.includes('transition')) return { label: 'Options flow', desc: 'Near gamma flip — transitional' };
    return { label: 'Options flow', desc: r.replace(/\([^)]*\)/g, '').trim() || 'Dealer gamma regime' };
  }
  if (factor === 'cot') {
    if (r.includes('stretched short')) return { label: 'Positioning', desc: 'Speculators heavily short — contrarian bullish' };
    if (r.includes('stretched long')) return { label: 'Positioning', desc: 'Speculators heavily long — contrarian bearish' };
    if (r.includes('neutral')) return { label: 'Positioning', desc: 'Speculator positioning neutral' };
    return { label: 'Positioning', desc: 'Speculator positioning' };
  }
  return { label: factor, desc: reason || '' };
}

const PolicyCard: React.FC<{ policy: Policy; expanded: boolean; onToggle: () => void }> = ({ policy, expanded, onToggle }) => {
  const symCount = policy.symbols?.length || 0;
  // For stock_signals strategy with no symbol filter, "0 symbols" means
  // "mirror every open signal" (the all_signals universe preset). Render
  // that intent in the summary cell instead of a misleading "0".
  const isMirrorAll = policy.strategyId === 'stock_signals' && symCount === 0;
  const universeLabel = isMirrorAll ? 'All open picks' : String(symCount);
  return (
    <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6 }}>
      <div onClick={onToggle} style={{
        cursor: 'pointer', padding: '0.7rem 0.9rem',
        display: 'grid', gridTemplateColumns: 'minmax(160px, 1.5fr) minmax(120px, 1fr) minmax(120px, 1fr) auto',
        gap: '0.75rem', alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: '0.95rem', fontWeight: 700, color: c.text }}>{policy.policyId}</div>
          <div style={{ fontSize: '0.72rem', color: c.muted, marginTop: 2 }}>strategy: {policy.strategyId}</div>
        </div>
        <Cell label="Risk" value={policy.riskAppetite} />
        <Cell label="Universe" value={universeLabel} />
        <div style={{ fontSize: '0.78rem', color: c.muted, textAlign: 'right' }}>{expanded ? '▼' : '▶'}</div>
      </div>
      {expanded && (
        <div style={{ padding: '0.5rem 0.9rem 0.85rem', borderTop: `1px solid ${c.border}`, background: c.cardAlt }}>
          <div style={{ fontSize: '0.7rem', color: c.muted, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Universe</div>
          {isMirrorAll ? (
            <div style={{ fontSize: '0.78rem', color: c.text, fontStyle: 'italic' }}>
              Mirrors every open pick in <strong>prod-stock-signals</strong> with no symbol filter.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
              {(policy.symbols || []).map((s) => (
                <span key={s} style={{
                  background: c.bg, border: `1px solid ${c.border}`, color: c.text,
                  fontSize: '0.72rem', padding: '0.15rem 0.45rem', borderRadius: 3, fontFamily: 'monospace',
                }}>{s}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BreakdownTable: React.FC<{ title: string; rows: Array<[string, { n: number; wins: number; pnl: number }]> }> = ({ title, rows }) => (
  <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6, padding: '0.7rem 0.85rem' }}>
    <div style={{ fontSize: '0.7rem', color: c.muted, fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</div>
    {rows.length === 0 ? <div style={{ color: c.muted, fontSize: '0.8rem' }}>—</div> : (
      <table style={{ width: '100%', fontSize: '0.82rem' }}>
        <thead>
          <tr style={{ color: c.dim, textAlign: 'left', fontSize: '0.7rem' }}>
            <th>Name</th><th style={{ textAlign: 'right' }}>n</th><th style={{ textAlign: 'right' }}>WR</th><th style={{ textAlign: 'right' }}>P&L</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([name, s]) => (
            <tr key={name} style={{ borderTop: `1px solid ${c.border}` }}>
              <td style={{ padding: '0.3rem 0' }}>{name}</td>
              <td style={{ textAlign: 'right' }}>{s.n}</td>
              <td style={{ textAlign: 'right' }}>{s.n > 0 ? `${((s.wins / s.n) * 100).toFixed(0)}%` : '—'}</td>
              <td style={{ textAlign: 'right', color: colorFor(s.pnl), fontWeight: 600 }}>{fmtMoney(s.pnl)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);
export default TtStockDashboard;
